import type { HeartRateEvent } from '@model/DeviceConfiguration';
import {
  countExpectedGapInsertions,
  detectHrDataIssues,
  normalizeTimestampToSecond,
} from './detectHrDataIssues';
import type {
  AiRepairOperations,
  DeterministicRepairValidation,
  RepairCheckResult,
} from './types';

const pass = (id: string, message: string): RepairCheckResult => ({
  id,
  passed: true,
  message,
});

const fail = (id: string, message: string): RepairCheckResult => ({
  id,
  passed: false,
  message,
});

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const eventSignature = (event: HeartRateEvent) =>
  JSON.stringify({
    hr: event.hr,
    hrv: event.hrv,
    lf: event.lf,
    hf: event.hf,
    deviceCode: event.deviceCode,
  });

export const validateRepairDeterministic = (
  originalEvents: HeartRateEvent[],
  repairedEvents: HeartRateEvent[],
  operations: AiRepairOperations
): DeterministicRepairValidation => {
  const checks: RepairCheckResult[] = [];
  const issues = detectHrDataIssues(originalEvents);
  const expectedGapInsertions = countExpectedGapInsertions(issues);
  const repairedBySecond = new Map<number, HeartRateEvent>();

  repairedEvents.forEach(event => {
    if (!isFiniteNumber(event.timestamp)) return;
    repairedBySecond.set(normalizeTimestampToSecond(event.timestamp), event);
  });

  const originalGrouped = new Map<number, HeartRateEvent[]>();
  originalEvents.forEach(event => {
    if (!isFiniteNumber(event.timestamp)) return;
    const second = normalizeTimestampToSecond(event.timestamp);
    const current = originalGrouped.get(second) ?? [];
    current.push(event);
    originalGrouped.set(second, current);
  });

  const duplicateSeconds = new Set(
    issues.duplicates.map(item => item.secondTimestamp)
  );

  let uniqueChecked = 0;
  originalGrouped.forEach((groupEvents, second) => {
    if (duplicateSeconds.has(second)) return;
    if (groupEvents.length !== 1) return;

    uniqueChecked += 1;
    const original = groupEvents[0];
    const match = repairedBySecond.get(second);

    if (!match) {
      checks.push(
        fail(
          `preserve-unique-${second}`,
          `Evento unico al secondo ${second} mancante nel risultato.`
        )
      );
      return;
    }

    if (eventSignature(original) !== eventSignature(match)) {
      checks.push(
        fail(
          `preserve-unique-${second}`,
          `Evento unico al secondo ${second} alterato nel risultato.`
        )
      );
      return;
    }

    if (match.repair || match.type === 'gap') {
      checks.push(
        fail(
          `preserve-unique-${second}`,
          `Evento unico al secondo ${second} marcato come riparato.`
        )
      );
    }
  });

  if (uniqueChecked > 0 && checks.every(check => check.passed)) {
    checks.push(
      pass(
        'preserve-unique-events',
        `${uniqueChecked} eventi unici conservati invariati.`
      )
    );
  }

  issues.duplicates.forEach(duplicate => {
    const event = repairedBySecond.get(duplicate.secondTimestamp);

    if (!event) {
      checks.push(
        fail(
          `duplicate-${duplicate.secondTimestamp}`,
          `Secondo ${duplicate.secondTimestamp}: merge assente nel risultato.`
        )
      );
      return;
    }

    if (event.repair?.type !== 'duplicate') {
      checks.push(
        fail(
          `duplicate-${duplicate.secondTimestamp}`,
          `Secondo ${duplicate.secondTimestamp}: merge senza repair.type duplicate.`
        )
      );
      return;
    }

    if (event.repair.sourceCount !== duplicate.events.length) {
      checks.push(
        fail(
          `duplicate-${duplicate.secondTimestamp}`,
          `Secondo ${duplicate.secondTimestamp}: sourceCount=${event.repair.sourceCount}, atteso ${duplicate.events.length}.`
        )
      );
      return;
    }

    checks.push(
      pass(
        `duplicate-${duplicate.secondTimestamp}`,
        `Duplicati al secondo ${duplicate.secondTimestamp} uniti correttamente.`
      )
    );
  });

  const gapEvents = repairedEvents.filter(
    event => event.repair?.type === 'gap'
  );
  if (gapEvents.length !== expectedGapInsertions) {
    checks.push(
      fail(
        'gap-count',
        `Gap inseriti=${gapEvents.length}, attesi=${expectedGapInsertions}.`
      )
    );
  } else {
    checks.push(
      pass(
        'gap-count',
        `Tutti i ${expectedGapInsertions} gap attesi sono stati colmati.`
      )
    );
  }

  let invalidGapEvents = 0;
  gapEvents.forEach(event => {
    if (event.type !== 'gap') {
      invalidGapEvents += 1;
      checks.push(
        fail(
          `gap-type-${event.timestamp}`,
          `Gap ${event.timestamp} senza type=gap.`
        )
      );
      return;
    }

    const fromTimestamp = event.repair?.fromTimestamp;
    const toTimestamp = event.repair?.toTimestamp;
    if (
      !isFiniteNumber(fromTimestamp) ||
      !isFiniteNumber(toTimestamp) ||
      event.timestamp <= fromTimestamp ||
      event.timestamp >= toTimestamp
    ) {
      invalidGapEvents += 1;
      checks.push(
        fail(
          `gap-range-${event.timestamp}`,
          `Gap ${event.timestamp} fuori range (${fromTimestamp}-${toTimestamp}).`
        )
      );
    }
  });

  if (gapEvents.length > 0 && invalidGapEvents === 0) {
    checks.push(
      pass(
        'gap-ranges',
        `${gapEvents.length} gap tracciati correttamente nel range temporale.`
      )
    );
  }

  const uniqueOriginalSeconds = originalGrouped.size;
  const expectedCount = uniqueOriginalSeconds + expectedGapInsertions;
  if (repairedEvents.length !== expectedCount) {
    checks.push(
      fail(
        'no-data-loss',
        `Count output=${repairedEvents.length}, atteso=${expectedCount} (${uniqueOriginalSeconds} secondi unici + ${expectedGapInsertions} gap).`
      )
    );
  } else {
    checks.push(
      pass(
        'no-data-loss',
        `Nessuna perdita: ${repairedEvents.length} eventi output attesi.`
      )
    );
  }

  const monotonic = repairedEvents.every(
    (event, index) =>
      index === 0 || event.timestamp >= repairedEvents[index - 1].timestamp
  );
  checks.push(
    monotonic
      ? pass('monotonic', 'Timestamps monotoni crescenti.')
      : fail('monotonic', 'Timestamps non monotoni nel risultato.')
  );

  const syntheticIds = repairedEvents.filter(event => event.id);
  checks.push(
    syntheticIds.length === 0
      ? pass('no-synthetic-id', 'Nessun id sintetico nei record riparati.')
      : fail(
          'no-synthetic-id',
          `${syntheticIds.length} record contengono id nel risultato riparato.`
        )
  );

  if (operations.duplicateMerges.length !== issues.duplicates.length) {
    checks.push(
      fail(
        'operations-duplicates',
        `Operazioni duplicate=${operations.duplicateMerges.length}, issues=${issues.duplicates.length}.`
      )
    );
  } else {
    checks.push(
      pass(
        'operations-duplicates',
        "Tutte le issue duplicate hanno un'operazione corrispondente."
      )
    );
  }

  if (operations.gapInsertions.length !== expectedGapInsertions) {
    checks.push(
      fail(
        'operations-gaps',
        `Operazioni gap=${operations.gapInsertions.length}, attese=${expectedGapInsertions}.`
      )
    );
  } else {
    checks.push(
      pass(
        'operations-gaps',
        'Tutti i gap attesi hanno operazioni di inserimento.'
      )
    );
  }

  return {
    passed: checks.every(check => check.passed),
    checks,
  };
};
